import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { messages, type Product } from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input, Textarea } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { SkeletonRows } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/feedback';
import { PageHeader } from '@/components/common/page';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/** S71 商材 */
export const SettingsProductsPage = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Product | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: qk.products(true),
    queryFn: () => api.get<SingleResponse<Product[]>>('/products', { includeArchived: true }),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.post(`/products/${id}/archive`),
    onSuccess: () => {
      toast.success('商材を停止しました');
      void queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <>
      <PageHeader
        back="/settings"
        title={messages.settings.products}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden />
            商材を登録する
          </Button>
        }
      />

      {isLoading ? (
        <SkeletonRows rows={3} />
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          title={messages.empty.products.title}
          description={messages.empty.products.description}
          action={{ label: messages.empty.products.action, onClick: () => setCreateOpen(true) }}
        />
      ) : (
        <ul className="space-y-3">
          {data.data.map((product) => (
            <li key={product.id}>
              <Card>
                <CardHeader>
                  <div className="min-w-0">
                    <CardTitle className="flex flex-wrap items-center gap-2">
                      {product.name}
                      {product.status === 'archived' && <Badge variant="dashed">停止中</Badge>}
                    </CardTitle>
                    <p className="mt-1 line-clamp-2 text-body text-foreground-muted">{product.summary}</p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditing(product)}>
                      {messages.common.edit}
                    </Button>
                    {product.status === 'active' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => archive.mutate(product.id)}
                        disabled={archive.isPending}
                      >
                        {messages.settings.archiveProduct}
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-2 text-body">
                    <div>
                      <dt className="text-note text-foreground-muted">{messages.settings.strengths}</dt>
                      <dd>
                        <ul className="mt-0.5 space-y-0.5">
                          {product.strengths.map((s) => (
                            <li key={s}>・{s}</li>
                          ))}
                        </ul>
                      </dd>
                    </div>
                    {product.ngPhrases && product.ngPhrases.length > 0 && (
                      <div>
                        <dt className="text-note text-foreground-muted">{messages.settings.ngPhrases}</dt>
                        <dd className="flex flex-wrap gap-1.5">
                          {product.ngPhrases.map((p) => (
                            <Badge key={p} variant="dashed">
                              {p}
                            </Badge>
                          ))}
                        </dd>
                      </div>
                    )}
                    {product.cases.length > 0 && (
                      <div>
                        <dt className="text-note text-foreground-muted">
                          {messages.settings.cases}（{product.cases.length}）
                        </dt>
                        <dd className="mt-0.5 space-y-1">
                          {product.cases.map((c) => (
                            <p key={c.id} className="flex items-center gap-2 text-note">
                              <span className="min-w-0 truncate">{c.title}</span>
                              {!c.isPublic && <Badge variant="dashed">文面で使用しない</Badge>}
                            </p>
                          ))}
                        </dd>
                      </div>
                    )}
                    {product.usedByCampaignCount > 0 && (
                      <p className="text-note text-foreground-muted">
                        {product.usedByCampaignCount} 件のキャンペーンで使用中
                      </p>
                    )}
                  </dl>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <ProductDialog
        open={createOpen || !!editing}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            setEditing(null);
          }
        }}
        product={editing}
      />
    </>
  );
};

const ProductDialog = ({
  open,
  onOpenChange,
  product,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
}) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: product?.name ?? '',
    summary: product?.summary ?? '',
    strengths: product?.strengths ?? [''],
    pricing: product?.pricing ?? '',
    pricingPublic: product?.pricingPublic ?? false,
    painPoints: product?.painPoints ?? [],
    ngPhrases: product?.ngPhrases ?? [],
  });

  // 開くたびに対象の内容へ差し替える
  const [initialized, setInitialized] = useState<string | null>(null);
  if (open && initialized !== (product?.id ?? 'new')) {
    setInitialized(product?.id ?? 'new');
    setForm({
      name: product?.name ?? '',
      summary: product?.summary ?? '',
      strengths: product?.strengths?.length ? product.strengths : [''],
      pricing: product?.pricing ?? '',
      pricingPublic: product?.pricingPublic ?? false,
      painPoints: product?.painPoints ?? [],
      ngPhrases: product?.ngPhrases ?? [],
    });
  }

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name,
        summary: form.summary,
        strengths: form.strengths.filter(Boolean),
        pricing: form.pricing || undefined,
        pricingPublic: form.pricingPublic,
        painPoints: form.painPoints.filter(Boolean),
        ngPhrases: form.ngPhrases.filter(Boolean),
        targetCriteria: product?.targetCriteria ?? {},
      };
      return product
        ? api.patch(`/products/${product.id}`, payload)
        : api.post('/products', payload);
    },
    onSuccess: () => {
      toast.success(product ? '商材を更新しました' : '商材を登録しました');
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      onOpenChange(false);
      setInitialized(null);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="lg:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{product ? '商材を編集する' : '商材を登録する'}</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-3">
          <Field label="商材名" required>
            {(props) => (
              <Input {...props} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            )}
          </Field>
          <Field label="概要" required hint="何を提供するか。200〜800 字を推奨">
            {(props) => (
              <Textarea
                {...props}
                rows={5}
                value={form.summary}
                onChange={(e) => setForm((p) => ({ ...p, summary: e.target.value }))}
              />
            )}
          </Field>

          <ListField
            label={messages.settings.strengths}
            required
            hint="3〜7 項目。文面生成の主要素材になります"
            values={form.strengths}
            onChange={(values) => setForm((p) => ({ ...p, strengths: values }))}
          />

          <Field label="価格情報">
            {(props) => (
              <Input {...props} value={form.pricing} onChange={(e) => setForm((p) => ({ ...p, pricing: e.target.value }))} />
            )}
          </Field>
          <label className="flex min-h-touch items-center gap-2 text-body">
            <input
              type="checkbox"
              className="size-4 accent-foreground"
              checked={form.pricingPublic}
              onChange={(e) => setForm((p) => ({ ...p, pricingPublic: e.target.checked }))}
            />
            {messages.settings.pricingPublic}
          </label>

          <ListField
            label={messages.settings.painPoints}
            values={form.painPoints}
            onChange={(values) => setForm((p) => ({ ...p, painPoints: values }))}
          />
          <ListField
            label={messages.settings.ngPhrases}
            hint="生成後の検査で照合し、含まれる場合は再生成します"
            values={form.ngPhrases}
            onChange={(values) => setForm((p) => ({ ...p, ngPhrases: values }))}
          />
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {messages.common.cancel}
          </Button>
          <Button
            size="lg"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !form.name.trim() || !form.summary.trim()}
          >
            {messages.common.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** 行追加式の入力 */
const ListField = ({
  label,
  values,
  onChange,
  required,
  hint,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  required?: boolean;
  hint?: string;
}) => (
  <Field label={label} required={required} hint={hint}>
    {() => (
      <div className="space-y-2">
        {values.map((value, index) => (
          <div key={index} className="flex gap-2">
            <Input
              value={value}
              onChange={(e) => onChange(values.map((v, i) => (i === index ? e.target.value : v)))}
              aria-label={`${label} ${index + 1}`}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onChange(values.filter((_, i) => i !== index))}
              aria-label={`${label} ${index + 1} を削除`}
            >
              <X aria-hidden />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={() => onChange([...values, ''])}>
          <Plus aria-hidden className="size-4" />
          行を追加する
        </Button>
      </div>
    )}
  </Field>
);
